import { useEffect, useState } from 'react';
import { useRoute, Link } from 'wouter';
import Navigation from '@/components/navigation';
import Footer from '@/components/footer';
import SEO from '@/components/SEO';
import { Calendar, User, ArrowLeft, Home } from 'lucide-react';

interface BlogPostData {
  slug: string;
  title: string;
  description: string;
  date: string;
  author: string;
  keywords: string;
  content: string;
}

export default function BlogPost() {
  const [, params] = useRoute('/blog/:slug');
  const [post, setPost] = useState<BlogPostData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (params?.slug) {
      fetch(`/api/blog/${params.slug}`)
        .then(res => {
          if (!res.ok) throw new Error('Post not found');
          return res.json();
        })
        .then(data => {
          setPost(data);
          setLoading(false);
        })
        .catch(error => {
          console.error('Error loading blog post:', error);
          setError(true);
          setLoading(false);
        });
    }
  }, [params?.slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="container mx-auto px-4 py-20 text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading article...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Article Not Found</h1>
          <p className="text-gray-600 mb-8">The blog post you're looking for doesn't exist.</p>
          <Link href="/blog">
            <a className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors">
              <ArrowLeft className="w-5 h-5" />
              Back to Blog
            </a>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={post.title}
        description={post.description}
        keywords={post.keywords}
        ogType="article"
        schema={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": post.title,
          "description": post.description,
          "author": {
            "@type": "Organization",
            "name": post.author
          },
          "datePublished": post.date,
          "dateModified": post.date,
          "publisher": {
            "@type": "Organization",
            "name": "Selling South FL Waterfront",
            "logo": {
              "@type": "ImageObject",
              "url": "https://sellingsouthflwaterfront.com/logo.png"
            }
          },
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": `https://sellingsouthflwaterfront.com/blog/${post.slug}`
          }
        }}
      />
      <Navigation />
      
      <article className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-gray-600 mb-8">
          <Link href="/">
            <a className="hover:text-blue-600 flex items-center gap-1">
              <Home className="w-4 h-4" />
              Home
            </a>
          </Link>
          <span>/</span>
          <Link href="/blog">
            <a className="hover:text-blue-600">Blog</a>
          </Link>
          <span>/</span>
          <span className="text-gray-900">{post.title.substring(0, 50)}...</span>
        </nav>

        {/* Article Header */}
        <header className="mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            {post.title}
          </h1>
          
          <div className="flex items-center gap-6 text-gray-600">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              <span>{new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
            </div>
            <div className="flex items-center gap-2">
              <User className="w-5 h-5" />
              <span>{post.author}</span>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-xl text-gray-700 leading-relaxed">
              {post.description}
            </p>
          </div>
        </header>

        {/* Article Content */}
        <div 
          className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-headings:font-bold prose-p:text-gray-700 prose-p:leading-relaxed prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline prose-strong:text-gray-900 prose-ul:text-gray-700 prose-ol:text-gray-700"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        {/* Internal Links CTA */}
        <div className="mt-16 p-8 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border border-blue-100">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Ready to Explore Waterfront Properties?
          </h3>
          <p className="text-gray-700 mb-6">
            Browse our exclusive collection of luxury oceanfront condos, bayfront estates, and waterfront homes across South Florida.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link href="/buy">
              <a className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors">
                View Properties
              </a>
            </Link>
            <Link href="/about">
              <a className="inline-block bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold border-2 border-blue-600 hover:bg-blue-50 transition-colors">
                Contact Our Team
              </a>
            </Link>
          </div>
        </div>

        {/* Back to Blog */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <Link href="/blog">
            <a className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold">
              <ArrowLeft className="w-5 h-5" />
              Back to All Articles
            </a>
          </Link>
        </div>
      </article>

      <Footer />
    </div>
  );
}

